import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  Plus,
  Minus,
  Search,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { useStock } from "@/context/StockContext";
import { Product } from "@/types/stock";
import { toast } from "sonner";

interface QuickStockAdjustmentProps {
  touchMode: boolean;
}

const QuickStockAdjustment: React.FC<QuickStockAdjustmentProps> = ({
  touchMode,
}) => {
  const { t } = useTranslation();
  const { products, updateProduct } = useStock();
  const [searchTerm, setSearchTerm] = useState("");
  const [adjustments, setAdjustments] = useState<Record<string, number>>({});

  // Filter products based on search
  const filteredProducts = products.filter((product) => {
    if (!searchTerm) return product.isActive;
    const search = searchTerm.toLowerCase();
    return (
      product.isActive &&
      (product.name.toLowerCase().includes(search) ||
        product.sku?.toLowerCase().includes(search) ||
        product.barcode?.toLowerCase().includes(search))
    );
  });

  // Get products that need attention (low stock or out of stock)
  const alertProducts = filteredProducts.filter(
    (p) => p.stockQuantity <= p.minStockLevel
  );

  const handleAdjustmentChange = (productId: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setAdjustments((prev) => ({
      ...prev,
      [productId]: numValue,
    }));
  };

  const handleQuickAdd = (productId: string, amount: number) => {
    setAdjustments((prev) => ({
      ...prev,
      [productId]: (prev[productId] || 0) + amount,
    }));
  };

  const handleApplyAdjustment = async (product: Product) => {
    const adjustment = adjustments[product.id] || 0;
    if (adjustment === 0) {
      toast.error(t("stock.noAdjustmentEntered"));
      return;
    }

    const newQuantity = product.stockQuantity + adjustment;
    if (newQuantity < 0) {
      toast.error(t("stock.cannotHaveNegativeStock"));
      return;
    }

    try {
      await updateProduct(product.id, {
        stockQuantity: newQuantity,
      });

      toast.success(
        `${product.name}: ${product.stockQuantity} → ${newQuantity} (${
          adjustment > 0 ? "+" : ""
        }${adjustment})`
      );

      // Clear adjustment
      setAdjustments((prev) => {
        const newAdj = { ...prev };
        delete newAdj[product.id];
        return newAdj;
      });
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleApplyAll = async () => {
    const productsToUpdate = Object.entries(adjustments).filter(
      ([_, adj]) => adj !== 0
    );

    if (productsToUpdate.length === 0) {
      toast.error(t("stock.noAdjustmentsToApply"));
      return;
    }

    let successCount = 0;
    for (const [productId, adjustment] of productsToUpdate) {
      const product = products.find((p) => p.id === productId);
      if (!product) continue;

      const newQuantity = product.stockQuantity + adjustment;
      if (newQuantity < 0) continue;

      try {
        await updateProduct(productId, {
          stockQuantity: newQuantity,
        });
        successCount++;
      } catch (error) {
        console.error("Error updating product:", error);
      }
    }

    toast.success(t("stock.adjustmentsApplied", { count: successCount }));
    setAdjustments({});
  };

  const getTotalAdjustments = () => {
    return Object.values(adjustments).filter((adj) => adj !== 0).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              {t("stock.quickStockAdjustment")}
            </CardTitle>
            {getTotalAdjustments() > 0 && (
              <Button
                onClick={handleApplyAll}
                size={touchMode ? "lg" : "default"}
              >
                {t("stock.applyAll")} ({getTotalAdjustments()})
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t("stock.searchProducts")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${touchMode ? "h-12 text-lg" : "h-10"}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Alert Products First */}
      {alertProducts.length > 0 && searchTerm === "" && (
        <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-500 text-base">
              <AlertCircle className="h-5 w-5" />
              {t("stock.needsAttention")} ({alertProducts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {alertProducts.slice(0, 5).map((product) => (
              <ProductAdjustmentRow
                key={product.id}
                product={product}
                adjustment={adjustments[product.id] || 0}
                onAdjustmentChange={handleAdjustmentChange}
                onQuickAdd={handleQuickAdd}
                onApply={handleApplyAdjustment}
                touchMode={touchMode}
                isAlert={true}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* All Products */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {searchTerm ? t("stock.searchResults") : t("stock.allProducts")} (
            {filteredProducts.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
          {filteredProducts.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {t("stock.noProductsFound")}
            </p>
          ) : (
            filteredProducts.map((product) => (
              <ProductAdjustmentRow
                key={product.id}
                product={product}
                adjustment={adjustments[product.id] || 0}
                onAdjustmentChange={handleAdjustmentChange}
                onQuickAdd={handleQuickAdd}
                onApply={handleApplyAdjustment}
                touchMode={touchMode}
                isAlert={product.stockQuantity <= product.minStockLevel}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Product Adjustment Row Component
interface ProductAdjustmentRowProps {
  product: Product;
  adjustment: number;
  onAdjustmentChange: (productId: string, value: string) => void;
  onQuickAdd: (productId: string, amount: number) => void;
  onApply: (product: Product) => void;
  touchMode: boolean;
  isAlert: boolean;
}

const ProductAdjustmentRow: React.FC<ProductAdjustmentRowProps> = ({
  product,
  adjustment,
  onAdjustmentChange,
  onQuickAdd,
  onApply,
  touchMode,
  isAlert,
}) => {
  const { t } = useTranslation();

  const getStockBadge = () => {
    if (product.stockQuantity === 0) {
      return <Badge variant="destructive">{t("stock.outOfStock")}</Badge>;
    }
    if (product.stockQuantity <= product.minStockLevel) {
      return <Badge className="bg-yellow-500">{t("stock.lowStock")}</Badge>;
    }
    return <Badge variant="secondary">{t("stock.inStock")}</Badge>;
  };

  const newQuantity = product.stockQuantity + adjustment;

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        isAlert
          ? "border-yellow-300 bg-yellow-50/50 dark:bg-yellow-900/5"
          : "border-gray-200 dark:border-gray-700"
      }`}
    >
      {/* Product Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-semibold truncate">{product.name}</p>
          {getStockBadge()}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("stock.current")}:{" "}
          <span className="font-semibold">{product.stockQuantity}</span>
          {adjustment !== 0 && (
            <span
              className={`ml-2 ${
                newQuantity >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              → {newQuantity}
            </span>
          )}
        </p>
      </div>

      {/* Quick Add Buttons */}
      <div className="flex gap-1">
        {[5, 10, 20].map((amount) => (
          <Button
            key={amount}
            variant="outline"
            size="sm"
            onClick={() => onQuickAdd(product.id, amount)}
            className="h-8 w-10 p-0 text-xs"
          >
            +{amount}
          </Button>
        ))}
      </div>

      {/* Adjustment Input */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onQuickAdd(product.id, -1)}
          className="h-8 w-8 p-0"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Input
          type="number"
          value={adjustment || ""}
          onChange={(e) => onAdjustmentChange(product.id, e.target.value)}
          className="w-20 h-8 text-center"
          placeholder="0"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => onQuickAdd(product.id, 1)}
          className="h-8 w-8 p-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Apply Button */}
      <Button
        onClick={() => onApply(product)}
        disabled={adjustment === 0}
        size={touchMode ? "default" : "sm"}
        className="min-w-[80px]"
      >
        {t("common.apply")}
      </Button>
    </div>
  );
};

export default QuickStockAdjustment;
